/**
 * Unit of Measure Modal Component - Using FormModal with Auto-Save
 */
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormModal } from '../../components/ui/FormModal';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';

const unitSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  abbreviation: z.string().min(1, 'Abreviação é obrigatória').max(10, 'Máximo 10 caracteres'),
  description: z.string().optional(),
  is_active: z.boolean().default(true),
});

export default function UnitOfMeasureModal({ open, onOpenChange, unit, onSave, loading }) {
  const isEditing = !!unit?.id;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(unitSchema),
    defaultValues: {
      name: '',
      abbreviation: '',
      description: '',
      is_active: true,
    },
  });

  // Reset form when modal opens or unit changes
  useEffect(() => {
    if (!open) return; // Only reset when modal is opening
    
    if (unit) {
      reset({
        name: unit.name || '',
        abbreviation: unit.abbreviation || '',
        description: unit.description || '',
        is_active: unit.is_active ?? true,
      });
    } else {
      reset({
        name: '',
        abbreviation: '',
        description: '',
        is_active: true,
      });
    }
  }, [open, unit, reset]);

  const formValues = watch();

  const handleLoadDraft = (draft) => {
    reset(draft);
  };

  const onSubmit = (data) => {
    onSave(data);
  };

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={isEditing ? 'Editar Unidade de Medida' : 'Nova Unidade de Medida'}
      description={isEditing ? 'Atualize as informações da unidade' : 'Preencha os dados da nova unidade'}
      size="default"
      formId="unit-of-measure"
      entityId={unit?.id}
      isDirty={isDirty}
      formValues={formValues}
      onLoadDraft={handleLoadDraft}
      onSave={handleSubmit(onSubmit)}
      loading={loading}
      confirmText="Salvar"
      cancelText="Cancelar"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome *</Label>
            <Input
              id="name"
              {...register('name')}
              placeholder="Ex: Quilograma"
              data-testid="unit-name"
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="abbreviation">Abreviação *</Label>
            <Input
              id="abbreviation"
              {...register('abbreviation')}
              placeholder="Ex: kg"
              maxLength={10}
              data-testid="unit-abbreviation"
            />
            {errors.abbreviation && (
              <p className="text-sm text-destructive">{errors.abbreviation.message}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Descrição</Label>
          <Textarea
            id="description"
            {...register('description')}
            placeholder="Descrição opcional..."
            rows={3}
          />
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="is_active"
            checked={watch('is_active')}
            onCheckedChange={(v) => setValue('is_active', v)}
          />
          <Label htmlFor="is_active">Unidade ativa</Label>
        </div>
      </div>
    </FormModal>
  );
}
